// +----------------------------------------------------------------------
// | EB [ EB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016~2024 https://www.eb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed EB并不是自由软件，未经许可不能去掉EB相关版权
// +----------------------------------------------------------------------
// | Author: EB Team <admin@eb.com>
// +----------------------------------------------------------------------
// eb-merchant
import { ElMessageBox, ElMessage } from 'element-plus'
import type { Action } from 'element-plus'

// 时间范围类型
export interface TimeRange {
  start: string
  end: string
  stock: number
}

// 表格分页参数类型
export interface TableFrom {
  page: number
  limit: number
  [key: string]: any
}

/**
 * 确认对话框
 * @param title 确认标题
 * @returns Promise
 */
export function modalSure(title?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    ElMessageBox.confirm(
      `确定${title || '删除该条数据吗'}?`,
      '提示',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    ).then(() => {
      resolve()
    }).catch((action: Action) => {
      if (action === 'cancel') {
        ElMessage({
          type: 'info',
          message: '已取消'
        })
      }
      reject(action)
    })
  })
}

/**
 * 删除确认对话框
 * @param title 确认标题
 * @returns Promise
 */
export function modalSureDelete(title?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    ElMessageBox.confirm(
      `${title || '该记录删除后不可恢复，您确认删除吗'}?`,
      '提示',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    ).then(() => {
      resolve()
    }).catch((action: Action) => {
      if (action === 'cancel') {
        ElMessage({
          type: 'info',
          message: '已取消'
        })
      }
      reject(action)
    })
  })
}

/**
 * 返回确认对话框
 * @param title 确认标题
 * @returns Promise
 */
export function returnSure(title?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    ElMessageBox.confirm(
      `确定${title || '删除该条数据吗'}?`,
      '提示',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    ).then(() => {
      resolve()
    }).catch((action: Action) => {
      if (action === 'cancel') {
        ElMessage({
          type: 'info',
          message: '已取消'
        })
      }
      reject(action)
    })
  })
}

/**
 * 处理表格删除最后一页唯一数据的操作
 * @param length 当前页数据长度
 * @param tableFrom 表格分页参数
 */
export function handleDeleteTable(length: number, tableFrom: TableFrom): void {
  if (length === 1 && tableFrom.page > 1) {
    tableFrom.page = tableFrom.page - 1
  }
}

/**
 * 解析时间字符串为毫秒数
 * @param time 时间字符串 (HH:mm)
 * @returns 毫秒数
 */
function parseTime(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return new Date(0, 0, 0, hours, minutes).getTime()
}

/**
 * 解析时间字符串为分钟数
 * @param timeStr 时间字符串 (HH:mm)
 * @returns 分钟数
 */
function parseTimeM(timeStr: string): number {
  // console.log('解析时间字符串为分钟数 parseTimeM',timeStr)
  const [hours, minutes] = timeStr.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * 时间分段计算
 * @param timeRange 时间范围 [startTime, endTime]
 * @param intervalMinutes 间隔分钟数
 * @returns 分段后的时间数组
 */
export function splitTimeRange(timeRange: [string, string], intervalMinutes: number): TimeRange[] {
  // console.log('splitTimeRange timeRange',timeRange)
  const [startTime, endTime] = timeRange
  const start = parseTime(startTime)
  const end = parseTime(endTime)
  const interval = intervalMinutes * 60 * 1000 // 转换为毫秒

  const result: TimeRange[] = []
  let currentStart = start

  while (currentStart + interval <= end) {
    const currentEnd = currentStart + interval
    const data: TimeRange = {
      start: formatTime(currentStart),
      end: formatTime(currentEnd),
      stock: 0
    }
    result.push(data)
    currentStart = currentEnd
  }

  // 处理最后一个时间段
  if (currentStart < end) {
    const remainingTime = end - currentStart
    if (remainingTime >= interval) {
      const data: TimeRange = {
        start: formatTime(currentStart),
        end: formatTime(end),
        stock: 0
      }
      result.push(data)
    }
  }

  // console.log('splitTimeRange result /',result)
  return result
}

/**
 * 格式化时间为 HH:mm 格式
 * @param time 毫秒数
 * @returns 格式化后的时间字符串
 */
function formatTime(time: number): string {
  const date = new Date(time)
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * 判断时间范围是否有交集
 * @param timeRanges 时间范围数组
 * @returns 是否有交集
 */
export function hasIntersection(timeRanges: [string, string][]): boolean {
  // console.log('判断时间范围是否有交集 timeRange',timeRanges)
  for (let i = 0; i < timeRanges.length; i++) {
    for (let j = i + 1; j < timeRanges.length; j++) {
      // console.log(timeRanges[i].map(parseTimeM), timeRanges[j].map(parseTimeM))
      const [start1, end1] = timeRanges[i].map(parseTimeM)
      const [start2, end2] = timeRanges[j].map(parseTimeM)
      if (start1 <= end2 && end1 >= start2) {
        return true
      }
    }
  }
  return false
}

/**
 * 判断时间范围是否递增
 * @param timeRanges 时间范围数组
 * @returns 是否递增
 */
export function isTimeRangesIncreasing(timeRanges: [string, string][]): boolean {
  // console.log('判断时间范围是否递增 timeRange',timeRanges)
  for (let i = 1; i < timeRanges.length; i++) {
    const [start1, end1] = timeRanges[i - 1].map(parseTimeM)
    const [start2, end2] = timeRanges[i].map(parseTimeM)
    if (end1 > start2) {
      return false
    }
  }
  return true
} 